/**
 * V68 인력 관리 — Tab 3: 고용형태 정보
 *
 * - 부서별 정규/인턴/파견/계약 + 총합
 * - 사용자 scope 별 KR/CN 표 분리 (system_admin/role_exec=두 표 / role_admin_kr=KR / role_admin_cn=CN / role_manager=본인 회사)
 * - 노출 부서 설정 (admin only — KR doc → role_admin_kr / CN doc → role_admin_cn / system_admin = 양쪽)
 * - 정렬: orgUnitName 가나다
 * - 0명 조직: 표시 (사용자 정책)
 */
import React, { useMemo, useState } from 'react';
import { Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import {
  useWorkforceEmploymentTypeInfo,
  CompanySection,
  AvailableOrg,
  CompanyCode,
  EmpTypeCounts,
  OrgRow,
} from '../../../shared/hooks/useWorkforceEmploymentTypeInfo';

// V68-FIX: compact table — 숫자 컬럼 위주 표 가독성 개선 (관리자 dense view).
const cellBase: React.CSSProperties = {
  padding: '6px 10px',
  borderBottom: '1px solid var(--color-gray-200)',
  fontSize: 13,
  textAlign: 'center',
  whiteSpace: 'nowrap',
};
const headerCell: React.CSSProperties = {
  padding: '8px 10px',
  background: 'var(--color-gray-100)',
  borderBottom: '2px solid var(--color-gray-300)',
  fontWeight: 600,
  fontSize: 13,
  textAlign: 'center',
  whiteSpace: 'nowrap',
};
const totalRow: React.CSSProperties = {
  background: 'var(--color-gray-50)',
  fontWeight: 600,
};
const sectionTitle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 600,
  marginTop: 24,
  marginBottom: 8,
  color: 'var(--color-gray-800)',
};

// V68: row total 대비 type 비율 표시. 정수 반올림.
//   count === 0 → '0' (% 미표시). count > 0 → '{count} ({pct}%)'.
function fmtCountPct(count: number, rowTotal: number): string {
  if (count === 0) return '0';
  const pct = rowTotal > 0 ? Math.round((count / rowTotal) * 100) : 0;
  return `${count} (${pct}%)`;
}

function CompanyTable({ title, section }: { title: string; section: CompanySection }) {
  return (
    // V68-FIX: 섹션 중앙 정렬 + overflow-x (작은 화면 가로 스크롤 안전).
    <div style={{ marginBottom: 24, overflowX: 'auto' }}>
      <h3 style={{ ...sectionTitle, textAlign: 'center' }}>{title}</h3>
      {/* V68-FIX: compact table 그대로 + margin auto 로 중앙 정렬. */}
      <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed', margin: '0 auto' }}>
        <colgroup>
          {/* V68: type 컬럼 4개는 '12 (60%)' 형식 표시를 위해 폭 확장 (64 → 88). total 컬럼은 % 표시 X → 72 유지. */}
          <col style={{ width: 260 }} />
          <col style={{ width: 88 }} />
          <col style={{ width: 88 }} />
          <col style={{ width: 88 }} />
          <col style={{ width: 88 }} />
          <col style={{ width: 72 }} />
        </colgroup>
        <thead>
          <tr>
            <th style={{ ...headerCell, textAlign: 'left' }}>부서</th>
            <th style={headerCell}>정규</th>
            <th style={headerCell}>인턴</th>
            <th style={headerCell}>파견</th>
            <th style={headerCell}>계약</th>
            <th style={headerCell}>총</th>
          </tr>
        </thead>
        <tbody>
          {section.rows.length === 0 && (
            <tr>
              <td colSpan={6} style={{ ...cellBase, padding: 24, color: 'var(--color-gray-500)' }}>
                노출 설정된 조직이 없습니다.
              </td>
            </tr>
          )}
          {section.rows.map((r: OrgRow) => (
            <tr key={r.orgUnitId}>
              {/* V68-FIX: 부서명 cell 만 wrap 허용 (한국어 keep-all). 숫자 cell 은 nowrap 유지. */}
              <td style={{ ...cellBase, textAlign: 'left', whiteSpace: 'normal', wordBreak: 'keep-all' }}>{r.orgUnitName}</td>
              <td style={cellBase}>{fmtCountPct(r.regular, r.total)}</td>
              <td style={cellBase}>{fmtCountPct(r.intern, r.total)}</td>
              <td style={cellBase}>{fmtCountPct(r.dispatch, r.total)}</td>
              <td style={cellBase}>{fmtCountPct(r.contract, r.total)}</td>
              <td style={cellBase}>{r.total}</td>
            </tr>
          ))}
          {section.rows.length > 0 && (
            <tr style={totalRow}>
              <td style={{ ...cellBase, textAlign: 'left' }}>합계</td>
              <td style={cellBase}>{fmtCountPct(section.summary.regular, section.summary.total)}</td>
              <td style={cellBase}>{fmtCountPct(section.summary.intern, section.summary.total)}</td>
              <td style={cellBase}>{fmtCountPct(section.summary.dispatch, section.summary.total)}</td>
              <td style={cellBase}>{fmtCountPct(section.summary.contract, section.summary.total)}</td>
              <td style={cellBase}>{section.summary.total}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

interface VisibilitySectionProps {
  companyCode: CompanyCode;
  availableOrgs: AvailableOrg[];
  visibility: Record<string, boolean>;
  onToggle: (companyCode: CompanyCode, orgUnitId: string, visible: boolean) => Promise<void>;
  pendingId: string | null;
}

function VisibilitySection({ companyCode, availableOrgs, visibility, onToggle, pendingId }: VisibilitySectionProps) {
  return (
    <div style={{ border: '1px solid var(--color-gray-200)', borderRadius: 6, padding: 12, minWidth: 240 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{companyCode}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 240, overflowY: 'auto' }}>
        {availableOrgs.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--color-gray-500)' }}>조직 없음</div>
        )}
        {availableOrgs.map(o => {
          const checked = visibility[o.orgUnitId] !== false; // default On
          const isPending = pendingId === o.orgUnitId;
          return (
            <label key={o.orgUnitId} style={{
              display: 'flex', alignItems: 'center', gap: 8, fontSize: 13,
              padding: '2px 4px', cursor: isPending ? 'wait' : 'pointer',
              opacity: isPending ? 0.6 : 1,
            }}>
              <input
                type="checkbox"
                checked={checked}
                disabled={isPending}
                onChange={e => onToggle(companyCode, o.orgUnitId, e.target.checked)}
              />
              <span>{o.name}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

export function EmploymentTypeInfoTab() {
  const { data, loading, error, updateVisibility } = useWorkforceEmploymentTypeInfo();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [opError, setOpError] = useState<string | null>(null);

  const canEditAny = useMemo(() => {
    return (data?.kr?.canEditVisibility || data?.cn?.canEditVisibility) || false;
  }, [data]);

  const handleToggle = async (companyCode: CompanyCode, orgUnitId: string, visible: boolean) => {
    setPendingId(orgUnitId);
    setOpError(null);
    const r = await updateVisibility(companyCode, orgUnitId, visible);
    if (!r.success) setOpError(r.error || '저장 실패');
    setPendingId(null);
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center' }}><Loader2 size={24} className="animate-spin" /></div>;
  }
  if (error) {
    return <div style={{ padding: 20, background: 'var(--color-red-50)', color: 'var(--color-red-700)', borderRadius: 6 }}>{error}</div>;
  }
  if (!data) return null;

  return (
    <div>
      {canEditAny && (
        <div style={{ marginBottom: 16, border: '1px solid var(--color-gray-200)', borderRadius: 6 }}>
          <button
            onClick={() => setSettingsOpen(s => !s)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 6,
              padding: '10px 12px', background: 'transparent', border: 'none',
              cursor: 'pointer', fontSize: 14, fontWeight: 600,
              borderBottom: settingsOpen ? '1px solid var(--color-gray-200)' : 'none',
            }}
          >
            {settingsOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            노출 부서 설정
          </button>
          {settingsOpen && (
            <div style={{ padding: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--color-gray-500)', marginBottom: 10 }}>
                체크 해제한 조직은 표에서 숨겨집니다. 기본값은 노출입니다.
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {data.kr?.canEditVisibility && (
                  <VisibilitySection
                    companyCode="KR"
                    availableOrgs={data.availableOrgs.kr}
                    visibility={data.kr.visibility}
                    onToggle={handleToggle}
                    pendingId={pendingId}
                  />
                )}
                {data.cn?.canEditVisibility && (
                  <VisibilitySection
                    companyCode="CN"
                    availableOrgs={data.availableOrgs.cn}
                    visibility={data.cn.visibility}
                    onToggle={handleToggle}
                    pendingId={pendingId}
                  />
                )}
              </div>
              {opError && (
                <div style={{
                  marginTop: 10, padding: 10, fontSize: 13,
                  background: 'var(--color-red-50)', color: 'var(--color-red-700)', borderRadius: 4,
                }}>
                  {opError}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {data.kr && <CompanyTable title="KR" section={data.kr} />}
      {data.cn && <CompanyTable title="CN" section={data.cn} />}
    </div>
  );
}
